import { useApolloClient } from '@apollo/client';
import { useCallback, useState } from 'react';
import isFakeAuthEnabled from './isFakeAuthEnabled';
import useOIDCAuth from './oidc/useOIDCAuth';

/**
 * If `REACT_APP_USE_FAKE_AUTH` is set to true, then this function will also
 * handle showing a modal to accept a username and call the `/auth/login`
 * endpoint.
 */
export default function useScoutAuth(): {
  isFakeAuthEnabled: boolean;
  isFakeAuthModalOpen: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  onFakeAuthModalDismiss: () => void;
} {
  const apolloClient = useApolloClient();
  const [isFakeAuthModalOpen, setIsFakeAuthModalOpen] = useState(false);
  const { login: oidcLogin, logout: oidcLogout } = useOIDCAuth();

  const onFakeAuthModalDismiss = useCallback(
    () => setIsFakeAuthModalOpen(false),
    [],
  );

  const login = useCallback(async () => {
    try {
      if (isFakeAuthEnabled()) {
        // For fake auth we just open the auth modal. Refreshing the
        // page after successful login is handled in the FakeAuthModal code.
        setIsFakeAuthModalOpen(true);
      } else {
        // Use proper OIDC flow
        await oidcLogin();
      }
    } catch (err) {
      console.error(err);
    }
  }, [oidcLogin]);

  const logout = useCallback(async () => {
    try {
      if (isFakeAuthEnabled()) {
        window.localStorage.removeItem('token');
        // Refresh the page after fake auth logout
        window.location.reload();
      } else {
        // Clear any cached state from apollo client
        await apolloClient.clearStore();
        
        // Use proper OIDC logout flow
        await oidcLogout();
      }
    } catch (err) {
      console.error(err);
    }
  }, [oidcLogout, apolloClient]);

  return {
    isFakeAuthModalOpen,
    login,
    logout,
    onFakeAuthModalDismiss,
    isFakeAuthEnabled: isFakeAuthEnabled(),
  };
}
